import { useEffect, type ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface SheetDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Width of the centered dialog on desktop. */
  widthClass?: string;
  /** Sticky footer (action buttons) rendered below the scrollable body. */
  footer?: ReactNode;
  zClass?: string;
}

/**
 * Modal that renders as a centered dialog on desktop and a full-width
 * bottom sheet (up to 92dvh) on mobile, so forms stay reachable one-handed.
 */
export default function SheetDialog({
  open,
  onClose,
  title,
  children,
  widthClass = 'sm:max-w-lg',
  footer,
  zClass = 'z-[11000]',
}: SheetDialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={clsx('fixed inset-0 flex sm:items-center sm:justify-center items-end', zClass)}>
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={clsx(
          'relative flex flex-col w-full bg-command-panel border border-command-border shadow-2xl',
          'max-h-[92dvh] rounded-t-2xl sm:rounded-2xl sm:m-4',
          widthClass
        )}
      >
        <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 pt-3 pb-3 border-b border-command-border">
          <div className="mx-auto sm:hidden absolute left-1/2 -translate-x-1/2 top-1.5 w-10 h-1 rounded-full bg-gray-600" />
          <div className="text-base sm:text-lg font-semibold text-gray-100 pt-1 sm:pt-0 min-w-0">{title}</div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 p-2.5 -mr-2 rounded-lg text-gray-400 hover:text-gray-100 hover:bg-command-border touch-manipulation"
            aria-label="Close dialog"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4">{children}</div>
        {footer && (
          <div className="flex-shrink-0 border-t border-command-border px-4 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
